import { useEffect, useState, useRef } from "react";
import SideDrawerOverlay from "../../SideOverLay";
import { Button } from "../../../common/components/Button";
import { Button as MuiButton } from "../../components/Common/Button";
import styles from "./index.module.scss";
import CheckBoxChecked from "../../../assets/images/checkBoxChecked.svg";
import CheckBoxUnchecked from "../../../assets/images/checkboxUnchecked.svg";
import RadioUnchecked from "../../../assets/images/radioUncheck.svg";
import MultiSelect from "../../../pages/SearchProp";
import dropdownDown from "../../../assets/images/dropdownDown.svg";
import StarIcon from "../../../assets/images/star.svg";
import DateRangePicker, { DateRange } from "rsuite/DateRangePicker";
import { colorizeMahatriaInfinitheism } from "../../../common/components/ColorizeMahatriaInfinitheism";

// Update interface for selected state
interface FilterValue {
  value: Array<{
    value: string;
    label: string;
  }> | {
    operator?: string;
    value?: string;
  };
  label: string;
}

interface Selected {
  [key: string]: FilterValue;
}

type AdminFilterOverlayProps = {
  open: boolean;
  onClose: () => void;
  onApply: (filters: any) => void;
  userRoles?: string[];
  initialFilters?: any;
  displayFilters?: any;
  customClass?: string;
};

const AdminFilterOverlay = ({
  open,
  onClose,
  userRoles = [],
  onApply,
  initialFilters,
  displayFilters,
  customClass = "",
}: AdminFilterOverlayProps) => {
  // If you want to add role-based filters, do it here before setFilters
  const [filters, setFilters] = useState<any[]>(displayFilters);
  // State for each filter type
  const [selected, setSelected] = useState<Selected>(initialFilters || {});
  const [rangeOperator, setRangeOperator] = useState(">=");
  const [rangeValue, setRangeValue] = useState("");
  const [selectedDropdowns, setSelectedDropdowns] = useState<{ [key: string]: string[] }>({});
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  // Dummy trigger for demonstration (replace with your form trigger if needed)
  const trigger = (_field?: string) => { };
  const clearDates = () => setDateRange([null, null]);
  const [placement, setPlacement] = useState<"bottomStart" | "topStart">(
    "bottomStart",
  );
  const [dateRange, setDateRange] = useState(null);
  const [dateError, setDateError] = useState<string | null>(null);

  const handleOpen = () => {
    if (calendarRef.current) {
      const rect = calendarRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Assume calendar height around 300px
      if (spaceBelow < 300 && spaceAbove > 300) {
        setPlacement("topStart");
      } else {
        setPlacement("bottomStart");
      }
    }

    setCalendarOpen(true);
  };

  // Handler for date range change
  const handleDateRangeChange = (value: DateRange | null) => {
    setDateRange(value);
    setDateError(null); // Clear error on change
  };
  useEffect(() => {
    if (initialFilters) {
      setSelected(initialFilters);
      // set other filter states if needed, e.g. range, dropdown, etc.

      // Prefill range values if present
      if (
        initialFilters.numberOfHdbs &&
        typeof initialFilters.numberOfHdbs === "object"
      ) {
        setRangeOperator(initialFilters.numberOfHdbs.operator || ">=");
        setRangeValue(initialFilters.numberOfHdbs.value || "");
      } else {
        setRangeOperator(">=");
        setRangeValue("");
      }

      // Prefill dropdown filters (handle multiple)
      const dropdownSelections: { [key: string]: string[] } = {};
      filters
        .filter((filter: any) => filter.type === "dropdown")
        .forEach((filter: any) => {
          let dropdownValues: string[] = [];
          const filterKey = filter.key;
          const filterInitial = initialFilters[filterKey];

          if (filterInitial) {
            if (Array.isArray(filterInitial)) {
              dropdownValues = filterInitial;
            } else if (
              filterInitial.value &&
              Array.isArray(filterInitial.value)
            ) {
              dropdownValues = filterInitial.value.map((item: any) => item.value);
            }
          }
          dropdownSelections[filterKey] = dropdownValues;
        });
      setSelectedDropdowns(dropdownSelections);

      // Prefill date range if present
      if (
        initialFilters.dateRange &&
        initialFilters.dateRange.startDate &&
        initialFilters.dateRange.endDate
      ) {
        const parseDate = (str: string) => {
          const [day, month, year] = str.split("/");
          return new Date(Number(year), Number(month) - 1, Number(day));
        };
        setDateRange([
          parseDate(initialFilters.dateRange.startDate),
          parseDate(initialFilters.dateRange.endDate),
        ]);
      } else {
        setDateRange([null, null]);
      }
    } else {
      setSelected({});
      setRangeOperator(">=");
      setRangeValue("");
      setSelectedDropdowns({});
      setDateRange([null, null]);
    }
  }, [initialFilters, open]);
  // Reset all filters
  const handleReset = () => {
    setSelected({});
    setRangeOperator(">=");
    setRangeValue("");
    setSelectedDropdowns({});
    setDateRange([null, null]); // Reset date range
    setDateError(null); // Clear any previous error
  };

  // First, create a type for the filter object
  type FilterObjectType = {
    [key: string]: {
      value: string | string[];
      label: string;
    };
  };

  // Update the handleApply function
  const handleApply = () => {
    // Validate date range
    if (dateRange && (dateRange[0] || dateRange[1])) {
      const isValidDate = (d: any) => d instanceof Date && !isNaN(d.getTime());
      if (!isValidDate(dateRange[0]) || !isValidDate(dateRange[1])) {
        setDateError("Please select a valid date range.");
        return;
      }
    }
    setDateError(null); // Clear any previous error

    const filterObj: FilterObjectType = {};

    // Update how we store filters to include labels
    Object.entries(selected).forEach(([key, value]) => {
      // Find the filter configuration to get the label
      const filterConfig = filters.find(filter => filter.key === key);
      if (filterConfig) {
        filterObj[key] = {
          value: value.value,
          label: filterConfig.label
        };
      }
    });

    // Store dropdown filters
    filters
      .filter((filter: any) => filter.type === "dropdown")
      .forEach((filter: any) => {
        filterObj[filter.key] = {
          label: filter.label,
          value: (selectedDropdowns[filter.key] || []).map(value => {
            const option = filter.options.find((opt: any) => opt.value === value);
            return {
              value: value,
              label: option?.label || value
            };
          })
        };
      });

    // Update date range to include label
    if (dateRange && dateRange[0] && dateRange[1]) {
      const formatDate = (date: Date) => {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      const dateFilter = filters.find(filter => filter.type === 'date');
      filterObj.dateRange = {
        value: {
          startDate: formatDate(dateRange[0] as Date),
          endDate: formatDate(dateRange[1] as Date)
        },
        label: dateFilter?.label || 'Date Range'
      };
    }

    if (onApply) onApply(filterObj);
    onClose();
  };

  // Update handleChange function
  const handleChange = (
    filterKey: string, 
    value: string, 
    type: string, 
    filterLabel: string, 
    optionLabel: string
  ) => {
    setSelected((prev) => {
      const updated = { ...prev };

      if (type === "checkbox") {
        const prevArr = Array.isArray(prev[filterKey]?.value)
          ? (prev[filterKey]?.value as Array<{ value: string; label: string }>)
          : [];

        const valueExists = prevArr.some(item => item.value === value);
        const newArr = valueExists
          ? prevArr.filter(item => item.value !== value)
          : [...prevArr, { value, label: optionLabel }];

        if (newArr.length === 0) {
          delete updated[filterKey];
        } else {
          updated[filterKey] = {
            value: newArr,
            label: filterLabel
          };
        }
      } else {
        updated[filterKey] = {
          value: { value, label: optionLabel },
          label: filterLabel
        };
      }

      return updated;
    });
  };

  useEffect(() => {
    if (!calendarOpen) return;
    const handleScroll = () => {
      setCalendarOpen(false);
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [calendarOpen]);

  return (
    <SideDrawerOverlay
      open={open}
      grayLine={true}
      onClose={onClose}
      headerText="Filters"
      customClass={customClass}
      footer={
        <>
          <MuiButton
            variant="outline"
            className={styles.cancelButton}
            onClick={handleReset}
          >
            reset
          </MuiButton>
          <Button
            buttonTextClassName={styles.saveButtonText}
            buttonClassName={styles.saveButton}
            onClick={handleApply}
          >
            <span className={styles.saveButtonTextCenter}>apply</span>
          </Button>
        </>
      }
    >
      <div className={styles.filterContainer}>
        {filters.map((filter: any) => (
          <div key={filter.key} className={styles.filterRow}>
            <div className={styles.filterFieldName}>
              {colorizeMahatriaInfinitheism(filter.label)}
            </div>
            <div className={styles.filterButtons}>
              {filter.type === "range" ? (
                <div className={styles.hdbFilterRow}>
                  <select
                    value={rangeOperator}
                    onChange={(e) => setRangeOperator(e.target.value)}
                    className={styles.hdbSelectWithIcon}
                  >
                    {filter.operators.map((option: any) => (
                      <option key={option?.value} value={option?.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min={0}
                    value={rangeValue}
                    onChange={(e) =>
                      setRangeValue(e.target.value.replace(/[^0-9]/g, ""))
                    }
                    className={styles.hdbInputFlex}
                  />
                </div>
              ) : filter.type === "date" ? (
                <div className={styles.datePickerWrapper} ref={calendarRef}>
                  <DateRangePicker
                    placement={placement}
                    value={dateRange}
                    onChange={(value) => {
                      handleDateRangeChange(value);
                      trigger("startDate");
                      trigger("endDate");
                    }}
                    ranges={[]}
                    format="dd/MM/yyyy"
                    onClean={() => setDateRange(null)}
                    placeholder="Select program date range"
                    style={{ width: "100%" }}
                    className={`${styles.datePicker} ${styles.datePickerGrayBorder}`}
                    onBlur={() => {
                      trigger("startDate");
                      trigger("endDate");
                    }}
                    onOpen={handleOpen}
                    onClose={() => setCalendarOpen(false)}
                    open={calendarOpen}
                  />
                  {dateError && (
                    <div className={styles.dateError}>{dateError}</div>
                  )}
                </div>
              ) : filter.type === "dropdown" ? (
                <MultiSelect
                  options={filter.options.map((opt: any) => ({
                    label: opt.label,
                    value: opt.value
                  }))}
                  selected={(selectedDropdowns[filter.key] || []).map(value => {
                    const option = filter.options.find((opt: any) => opt.value === value);
                    return {
                      label: option?.label || value,
                      value: value
                    };
                  })}
                  setSelected={(selected) => {
                    setSelectedDropdowns(prev => ({
                      ...prev,
                      [filter.key]: selected.map(item => item.value)
                    }));
                  }}
                  closeOnClickOutside={true}
                  placeholder={filter.label}
                />
              ) : (
                filter.options.map((option: any) => (
                  <label key={option?.value} className={styles.filterButton}>
                    <input
                      type={filter.type}
                      name={filter.key}
                      checked={
                        filter.type === "checkbox"
                          ? Array.isArray(selected[filter.key]?.value)
                            ? (selected[filter.key]?.value as Array<{ value: string }>)
                              .some(item => item.value === option?.value)
                            : false
                          : selected[filter.key]?.value?.value === option?.value
                      }
                      value={option?.value}
                      onChange={() =>
                        handleChange(
                          filter.key,
                          option?.value,
                          filter.type,
                          filter.label,
                          option?.label
                        )
                      }
                      style={{ display: "none" }}
                    />
                    <img
                      src={
                        filter.type === "checkbox"
                          ? Array.isArray(selected[filter.key]?.value)
                            ? (selected[filter.key]?.value as Array<{ value: string }>)
                              .some(item => item.value === option?.value)
                              ? CheckBoxChecked
                              : CheckBoxUnchecked
                            : CheckBoxUnchecked
                          : selected[filter.key]?.value?.value === option?.value
                            ? "RadioChecked"
                            : RadioUnchecked
                      }
                      alt={
                        filter.type === "checkbox"
                          ? Array.isArray(selected[filter.key]?.value)
                            ? (selected[filter.key]?.value as Array<{ value: string }>)
                              .some(item => item.value === option?.value)
                              ? "Checked"
                              : "Unchecked"
                            : "Unchecked"
                          : selected[filter.key]?.value?.value === option?.value
                            ? "Checked"
                            : "Unchecked"
                      }
                      className={
                        filter.type === "checkbox"
                          ? styles.customCheckbox
                          : styles.customRadio
                      }
                    />
                    <div className={styles.starContainer}>
                      {filter.type === "checkbox" &&
                        filter.label === "RM rating" &&
                        option.label !== "Yet to review" && (
                          <img
                            src={StarIcon}
                            alt="star"
                            className={styles.starIcon}
                          />
                        )}
                      <span className={styles.filterOptionLabel}>
                        {colorizeMahatriaInfinitheism(option.label)}
                      </span>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </SideDrawerOverlay>
  );
};

export default AdminFilterOverlay;
